import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MasterService } from 'app/service/master.service';
import { UserService } from 'app/service/user.service';
import { SupplementFormMetadataLoader } from 'app/shared/metadata/supplement-form-metadata.loader';
import { environment } from 'environments/environment';
import {
  SUP_BULK_MAX_ROWS,
  SUP_BULK_STATUS_OPTIONS,
  SUPPLEMENT_BULK_MEDIA_FOLDERS,
  SupBulkAssignment,
  SupBulkImportItem,
  SupBulkLookups,
  SupBulkParseResult,
  SupBulkPreviewRow,
} from './super-admin-supplement-bulk-upload.models';
import {
  applySupCsvMasterMappingsToRows,
  applyGlobalStatusToSupRows,
  downloadSuperAdminSupplementTemplate,
  mapSupplementSnapshotToLookups,
  rebuildSupplementBulkPayload,
  refreshSupBulkParseCounts,
  validateSupBulkCsv,
  validateSupBulkExcel,
} from './super-admin-supplement-bulk-upload.util';
import { SuperAdminSupplementBulkUploadRowEditorComponent } from './super-admin-supplement-bulk-upload-row-editor.component';

export type SupBulkTemplateFormat = 'csv' | 'xlsx';

@Component({
  selector: 'app-super-admin-supplement-bulk-upload-modal',
  templateUrl: './super-admin-supplement-bulk-upload-modal.component.html',
  styleUrls: ['./super-admin-supplement-bulk-upload-modal.component.scss'],
})
export class SuperAdminSupplementBulkUploadModalComponent implements OnInit, AfterViewInit {
  readonly maxRows = SUP_BULK_MAX_ROWS;
  readonly statusOptions = SUP_BULK_STATUS_OPTIONS.map((s) => ({ value: s, label: s }));
  readonly assignmentPriorities = ['High', 'Medium', 'Low'];

  @ViewChild('assignModalTpl') assignModalTpl!: TemplateRef<unknown>;

  step: 'upload' | 'preview' = 'upload';
  loadingLookups = false;
  parsing = false;
  fileName = '';
  fileError = '';
  parseResult: SupBulkParseResult | null = null;
  globalStatus = 'Draft';
  lookups: SupBulkLookups = {
    dietaryPreferences: [],
    dataSources: [],
    databaseSources: [],
    countries: [],
    tastes: [],
    flavours: [],
    allergens: [],
    allergenFree: [],
    intolerances: [],
    intoleranceFree: [],
    fssaiCategories: [],
    eStoreCategories: [],
    units: [],
    dosageUnits: [],
    supplementForms: [],
    nutrients: [],
    nutrientGroups: [],
    nutrientTags: [],
    claimTypes: [],
    claimTags: [],
    productBenefits: [],
    productBenefitTags: [],
    certificateOptions: [],
    licenseOptions: [],
  };

  assignMode: 'maker-and-checker' | 'checker-only' = 'maker-and-checker';
  assignTargetRows: SupBulkPreviewRow[] = [];
  assignDialogIsBulk = false;
  selectedMakerEmail = '';
  selectedCheckerEmail = '';
  selectedDueDate = '';
  selectedPriority = 'Medium';
  selectedNotes = '';
  makerList: any[] = [];
  checkerList: any[] = [];
  isMakerLoading = false;
  isCheckerLoading = false;
  private assignModalRef: NgbModalRef | null = null;
  private statusBeforeAssign = 'Draft';
  private lastNonAssignGlobalStatus = 'Draft';
  private assigningGlobal = false;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private masterService: MasterService,
    private metadataLoader: SupplementFormMetadataLoader,
    private userService: UserService
  ) {}

  get subtitle(): string {
    return this.step === 'upload'
      ? 'Download the CSV or XLSX template, fill any fields (all optional), then upload. Masters auto-map when names match.'
      : 'Review rows, optionally configure dropdowns, set status (default Draft), then import. All rows are accepted.';
  }

  get validCount(): number {
    return this.parseResult?.validCount || 0;
  }

  get errorCount(): number {
    return this.parseResult?.errorCount || 0;
  }

  get totalCount(): number {
    return this.parseResult?.totalCount || 0;
  }

  get assignTargetProduct(): SupBulkPreviewRow | null {
    return !this.assignDialogIsBulk && this.assignTargetRows.length === 1
      ? this.assignTargetRows[0]
      : null;
  }

  async ngOnInit(): Promise<void> {
    await this.loadLookups();
    this.scheduleLucideIconRender();
  }

  ngAfterViewInit(): void {
    this.scheduleLucideIconRender();
  }

  downloadTemplate(format: SupBulkTemplateFormat = 'xlsx'): void {
    downloadSuperAdminSupplementTemplate(format);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');
    const isCsv = lowerName.endsWith('.csv');
    if (!isExcel && !isCsv) {
      this.fileError = 'Please upload a valid CSV or Excel (.xlsx / .xls) file.';
      input.value = '';
      return;
    }

    this.fileError = '';
    this.fileName = file.name;
    this.parsing = true;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        this.parseResult = isExcel
          ? validateSupBulkExcel(reader.result as ArrayBuffer)
          : validateSupBulkCsv(String(reader.result || ''));
        if (this.parseResult.fileError) {
          this.fileError = this.parseResult.fileError;
          this.step = 'upload';
        } else if (!this.parseResult.totalCount) {
          this.fileError = 'No data rows found in the file.';
          this.step = 'upload';
        } else {
          applySupCsvMasterMappingsToRows(this.parseResult.rows, this.lookups);
          applyGlobalStatusToSupRows(this.parseResult.rows, this.globalStatus);
          this.refreshAllRows();
          this.step = 'preview';
        }
      } catch (err) {
        console.error('Failed to parse supplement bulk file', err);
        this.fileError = 'Failed to parse file.';
        this.step = 'upload';
      } finally {
        this.parsing = false;
        this.scheduleLucideIconRender();
      }
    };
    reader.onerror = () => {
      this.parsing = false;
      this.fileError = 'Failed to read file.';
    };
    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
    input.value = '';
  }

  backToUpload(): void {
    this.step = 'upload';
    this.parseResult = null;
    this.fileName = '';
    this.fileError = '';
    this.scheduleLucideIconRender();
  }

  onGlobalStatusChange(): void {
    if (!this.parseResult?.rows?.length) return;
    const next = this.globalStatus;

    if (this.isAssignmentStatus(next)) {
      this.statusBeforeAssign = this.lastNonAssignGlobalStatus || 'Draft';
      applyGlobalStatusToSupRows(this.parseResult.rows, next);
      this.refreshAllRows();
      void this.openAssignDialog(this.parseResult.rows, next, true);
      return;
    }

    this.lastNonAssignGlobalStatus = next;
    applyGlobalStatusToSupRows(this.parseResult.rows, next);
    this.refreshAllRows();
  }

  onRowStatusChange(row: SupBulkPreviewRow): void {
    const next = row.selections.status;
    if (this.isAssignmentStatus(next)) {
      this.statusBeforeAssign = 'Draft';
      if (
        next === 'Assigned to Maker' &&
        (!row.selections.assignment?.makerEmail || !row.selections.assignment?.checkerEmail)
      ) {
        row.selections.assignment = null;
      }
      if (next === 'Assigned to Checker' && !row.selections.assignment?.checkerEmail) {
        row.selections.assignment = null;
      }
      rebuildSupplementBulkPayload(row, this.lookups);
      refreshSupBulkParseCounts(this.parseResult!);
      void this.openAssignDialog([row], next, false);
      return;
    }

    row.selections.assignment = null;
    rebuildSupplementBulkPayload(row, this.lookups);
    refreshSupBulkParseCounts(this.parseResult!);
  }

  private isAssignmentStatus(status: string): boolean {
    return status === 'Assigned to Maker' || status === 'Assigned to Checker';
  }

  private async openAssignDialog(
    rows: SupBulkPreviewRow[],
    status: string,
    isBulk: boolean
  ): Promise<void> {
    this.assignMode = status === 'Assigned to Checker' ? 'checker-only' : 'maker-and-checker';
    this.assignTargetRows = rows;
    this.assignDialogIsBulk = isBulk || rows.length > 1;
    this.assigningGlobal = isBulk;

    const existing = rows[0]?.selections?.assignment;
    this.selectedMakerEmail = existing?.makerEmail || '';
    this.selectedCheckerEmail = existing?.checkerEmail || '';
    this.selectedDueDate = existing?.dueDate || '';
    this.selectedPriority = existing?.priority || 'Medium';
    this.selectedNotes = existing?.notes || '';

    if (this.assignMode === 'maker-and-checker') {
      void this.loadRoleUserList('maker');
      void this.loadRoleUserList('checker');
    } else {
      void this.loadRoleUserList('checker');
    }

    try {
      this.assignModalRef?.dismiss('reopen');
    } catch {
      /* noop */
    }

    this.assignModalRef = this.modalService.open(this.assignModalTpl, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: true,
      windowClass: 'recipe-assign-modal-window saru-assign-window',
    });
    this.scheduleLucideIconRender();

    this.assignModalRef.result.then(
      () => {
        this.assignModalRef = null;
      },
      (reason) => {
        if (reason !== 'assigned') {
          this.onAssignCancelled();
        }
        this.assignModalRef = null;
      }
    );
  }

  private onAssignCancelled(): void {
    if (this.assigningGlobal && this.parseResult?.rows?.length) {
      this.globalStatus = this.statusBeforeAssign || 'Draft';
      applyGlobalStatusToSupRows(this.parseResult.rows, this.globalStatus);
      this.refreshAllRows();
    } else if (this.assignTargetRows.length === 1) {
      const row = this.assignTargetRows[0];
      if (!row.selections.assignment) {
        row.selections.status = this.statusBeforeAssign || 'Draft';
        row.selections.assignment = null;
        rebuildSupplementBulkPayload(row, this.lookups);
        refreshSupBulkParseCounts(this.parseResult!);
      }
    }
    this.assignTargetRows = [];
    this.assigningGlobal = false;
  }

  confirmAssignment(modal?: { close: (r?: string) => void }): void {
    if (this.assignMode === 'maker-and-checker') {
      if (!this.selectedMakerEmail || !this.selectedCheckerEmail) return;
    } else if (!this.selectedCheckerEmail) {
      return;
    }

    const assignment: SupBulkAssignment = {
      mode: this.assignMode,
      makerEmail: this.assignMode === 'maker-and-checker' ? this.selectedMakerEmail : null,
      checkerEmail: this.selectedCheckerEmail,
      dueDate: this.selectedDueDate || null,
      priority: this.selectedPriority || 'Medium',
      notes: this.selectedNotes || null,
    };

    for (const row of this.assignTargetRows) {
      row.selections.assignment = { ...assignment };
      row.selections.status =
        this.assignMode === 'maker-and-checker' ? 'Assigned to Maker' : 'Assigned to Checker';
      rebuildSupplementBulkPayload(row, this.lookups);
    }

    if (this.assigningGlobal) {
      this.globalStatus =
        this.assignMode === 'maker-and-checker' ? 'Assigned to Maker' : 'Assigned to Checker';
    }

    refreshSupBulkParseCounts(this.parseResult!);
    modal?.close('assigned');
    this.assignModalRef = null;
    this.assignTargetRows = [];
    this.assigningGlobal = false;
    this.scheduleLucideIconRender();
  }

  closeAssignDialog(modal?: { dismiss: (r?: string) => void }): void {
    modal?.dismiss('cancel');
  }

  getAssignTargetCount(): number {
    return this.assignTargetRows.length;
  }

  private async loadRoleUserList(role: 'maker' | 'checker'): Promise<void> {
    const isMaker = role === 'maker';
    if (isMaker) {
      this.isMakerLoading = true;
      this.makerList = [];
    } else {
      this.isCheckerLoading = true;
      this.checkerList = [];
    }

    try {
      const res: any = await this.userService.getUsers({
        pageNo: 1,
        pageSize: 200,
        searchString: '',
        roleId: isMaker ? 5 : 4,
      });
      if (res && res.status === 0 && res.response) {
        const users = res.response.userList || res.response.data || res.response || [];
        if (isMaker) this.makerList = users;
        else this.checkerList = users;
      }
    } catch (error) {
      console.error(`Failed to load ${role} list:`, error);
    } finally {
      if (isMaker) this.isMakerLoading = false;
      else this.isCheckerLoading = false;
    }
  }

  async onRowImageSelected(event: Event, row: SupBulkPreviewRow): Promise<void> {
    await this.uploadRowAsset(event, row, 'frontImage', 'frontImageUploading', SUPPLEMENT_BULK_MEDIA_FOLDERS.packaging);
  }

  async onRowCertificateSelected(event: Event, row: SupBulkPreviewRow): Promise<void> {
    await this.uploadRowAsset(
      event,
      row,
      'authorizedCertificateImage',
      'authorizedCertificateUploading',
      SUPPLEMENT_BULK_MEDIA_FOLDERS.certificate
    );
  }

  async onRowLicenseSelected(event: Event, row: SupBulkPreviewRow): Promise<void> {
    await this.uploadRowAsset(
      event,
      row,
      'authorizedLicenseImage',
      'authorizedLicenseUploading',
      SUPPLEMENT_BULK_MEDIA_FOLDERS.license
    );
  }

  private async uploadRowAsset(
    event: Event,
    row: SupBulkPreviewRow,
    valueKey: 'frontImage' | 'authorizedCertificateImage' | 'authorizedLicenseImage',
    loadingKey: 'frontImageUploading' | 'authorizedCertificateUploading' | 'authorizedLicenseUploading',
    folder: string
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      this.fileError = `Invalid image type for row #${row.rowNumber}. Use PNG, JPEG, or WebP.`;
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.fileError = `Image too large for row #${row.rowNumber}. Max 5MB.`;
      input.value = '';
      return;
    }

    this.fileError = '';
    row.selections[loadingKey] = true;
    try {
      const url = await this.masterService.uploadMedia(file, folder);
      if (url) {
        row.selections[valueKey] = String(url);
        rebuildSupplementBulkPayload(row, this.lookups);
        refreshSupBulkParseCounts(this.parseResult!);
      } else {
        this.fileError = `Failed to upload image for row #${row.rowNumber}.`;
      }
    } catch (err) {
      console.error('Row image upload failed', err);
      this.fileError = `Failed to upload image for row #${row.rowNumber}.`;
    } finally {
      row.selections[loadingKey] = false;
      input.value = '';
      this.scheduleLucideIconRender();
    }
  }

  removeRowImage(row: SupBulkPreviewRow): void {
    row.selections.frontImage = null;
    rebuildSupplementBulkPayload(row, this.lookups);
    refreshSupBulkParseCounts(this.parseResult!);
    this.scheduleLucideIconRender();
  }

  removeRowCertificate(row: SupBulkPreviewRow): void {
    row.selections.authorizedCertificateImage = null;
    rebuildSupplementBulkPayload(row, this.lookups);
    refreshSupBulkParseCounts(this.parseResult!);
  }

  removeRowLicense(row: SupBulkPreviewRow): void {
    row.selections.authorizedLicenseImage = null;
    rebuildSupplementBulkPayload(row, this.lookups);
    refreshSupBulkParseCounts(this.parseResult!);
  }

  getRowImageUrl(row: SupBulkPreviewRow): string | null {
    return this.resolveUploadUrl(row?.selections?.frontImage);
  }

  getRowCertificateUrl(row: SupBulkPreviewRow): string | null {
    return this.resolveUploadUrl(row?.selections?.authorizedCertificateImage);
  }

  getRowLicenseUrl(row: SupBulkPreviewRow): string | null {
    return this.resolveUploadUrl(row?.selections?.authorizedLicenseImage);
  }

  getErrorSummary(error?: string | null): string {
    const parts = String(error || '')
      .split(/;\s*/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return '—';
    if (parts.length === 1) {
      const one = parts[0];
      return one.length > 42 ? one.slice(0, 40) + '…' : one;
    }
    return `${parts.length} issues — hover for details`;
  }

  formatErrorTooltip(error?: string | null): string {
    return String(error || '')
      .split(/;\s*/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p, i) => `${i + 1}. ${p}`)
      .join('\n');
  }

  private resolveUploadUrl(rawValue?: string | null): string | null {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw) || /^blob:/i.test(raw) || /^data:/i.test(raw)) return raw;
    if (raw.startsWith('//')) return `https:${raw}`;
    if (raw.startsWith('assets/')) return raw;

    const base = (environment.baseUrl || '').replace(/\/$/, '');
    let path = raw.replace(/^\//, '');
    if (!path.includes('/') && !path.startsWith('uploads/')) {
      path = `uploads/${path}`;
    }
    return base ? `${base}/${path}` : `/${path}`;
  }

  openRowEditor(row: SupBulkPreviewRow): void {
    const modalRef = this.modalService.open(SuperAdminSupplementBulkUploadRowEditorComponent, {
      size: 'xl',
      backdrop: 'static',
      centered: true,
      windowClass: 'super-admin-bulk-row-editor-window',
    });
    modalRef.componentInstance.row = row;
    modalRef.componentInstance.lookups = this.lookups;

    modalRef.result
      .then((updated: SupBulkPreviewRow) => {
        if (!updated) return;
        rebuildSupplementBulkPayload(updated, this.lookups);
        refreshSupBulkParseCounts(this.parseResult!);
        if (this.isAssignmentStatus(updated.selections.status) && !updated.selections.assignment) {
          void this.openAssignDialog([updated], updated.selections.status, false);
        }
        this.scheduleLucideIconRender();
      })
      .catch(() => {});
  }

  deleteRow(index: number): void {
    if (!this.parseResult?.rows?.length) return;
    this.parseResult.rows.splice(index, 1);
    this.parseResult.rows.forEach((r, i) => {
      r.rowNumber = i + 1;
    });
    if (!this.parseResult.rows.length) {
      this.backToUpload();
      return;
    }
    this.refreshAllRows();
    this.scheduleLucideIconRender();
  }

  confirmImport(): void {
    const validItems: SupBulkImportItem[] = (this.parseResult?.rows || [])
      .filter((r) => r.valid && r.payload)
      .map((r) => ({
        payload: r.payload as Record<string, unknown>,
        assignment: r.selections.assignment || null,
      }));
    if (!validItems.length) return;
    this.activeModal.close(validItems);
  }

  private refreshAllRows(): void {
    if (!this.parseResult) return;
    this.parseResult.rows.forEach((row) => {
      rebuildSupplementBulkPayload(row, this.lookups);
    });
    refreshSupBulkParseCounts(this.parseResult);
  }

  private async loadLookups(): Promise<void> {
    this.loadingLookups = true;
    try {
      const snapshot = await this.metadataLoader.load();
      this.lookups = mapSupplementSnapshotToLookups(snapshot);
    } catch (err) {
      console.error('Failed to load supplement bulk upload lookups', err);
    } finally {
      this.loadingLookups = false;
    }
  }

  private scheduleLucideIconRender(): void {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if ((window as any).lucide?.createIcons) {
        (window as any).lucide.createIcons();
      }
    }, 50);
  }
}
